import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { id } from 'date-fns/locale';
import billService, { CreateBillData } from '../services/billService';
import customerService from '../services/customerService';
import packageService from '../services/packageService';
import { Customer, Package } from '../types';

// Extended Customer interface for this component
interface CustomerWithPackage extends Customer {
  activation_date?: string;
  package?: {
    id: string;
    name: string;
    price: number | string;
  };
}

const parsePackagePrice = (price?: number | string | null): number => {
  const num = Number(price ?? 0);
  return Number.isFinite(num) ? num : 0;
};

const formatPackagePrice = (price?: number | string | null): string =>
  parsePackagePrice(price).toLocaleString('id-ID');

interface CreateBillDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateBillDialog: React.FC<CreateBillDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<CustomerWithPackage[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithPackage | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const [formData, setFormData] = useState<CreateBillData>({
    customer_id: '',
    package_id: '',
    billing_period_start: '',
    billing_period_end: '',
    amount: 0,
    tax_amount: 0,
    discount_amount: 0,
    notes: '',
  });

  // Generate months options
  const monthOptions = [
    { value: 1, label: 'Januari' },
    { value: 2, label: 'Februari' },
    { value: 3, label: 'Maret' },
    { value: 4, label: 'April' },
    { value: 5, label: 'Mei' },
    { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' },
    { value: 8, label: 'Agustus' },
    { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' },
    { value: 11, label: 'November' },
    { value: 12, label: 'Desember' },
  ];

  // Generate year options (current year and next 2 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = [
    currentYear,
    currentYear + 1,
    currentYear + 2,
  ];

  useEffect(() => {
    if (open) {
      fetchCustomers();
      fetchPackages();
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (selectedCustomer) {
      setFormData(prev => ({
        ...prev,
        customer_id: selectedCustomer.id,
        package_id: selectedCustomer.package?.id || '',
        amount: parsePackagePrice(selectedCustomer.package?.price),
      }));
      
      if (selectedCustomer.package) {
        const pkg = packages.find(p => p.id === selectedCustomer.package?.id);
        setSelectedPackage(pkg || null);
      }

      // Calculate billing period based on activation date
      calculateBillingPeriod();
    }
  }, [selectedCustomer, packages, selectedMonth, selectedYear]);

  const calculateBillingPeriod = async () => {
    if (!selectedCustomer?.activation_date) return;

    try {
      const response = await billService.getBillingPeriod(
        selectedCustomer.id,
        selectedMonth,
        selectedYear
      );

      if (response.success) {
        setFormData(prev => ({
          ...prev,
          billing_period_start: response.data.billing_period.start,
          billing_period_end: response.data.billing_period.end,
        }));
      }
    } catch (error) {
      console.error('Error calculating billing period:', error);
      // Fallback to local calculation if API fails
      const activationDate = new Date(selectedCustomer.activation_date);
      const activationDay = activationDate.getDate();

      let billingStart = new Date(selectedYear, selectedMonth - 1, activationDay);
      
      if (billingStart.getDate() !== activationDay) {
        billingStart = new Date(selectedYear, selectedMonth, 0);
      }

      let billingEnd = new Date(selectedYear, selectedMonth, activationDay - 1);
      
      // Handle edge cases for end date
      if (activationDay === 1) {
        // If activation day is 1st, end date should be last day of current month
        billingEnd = new Date(selectedYear, selectedMonth, 0);
      } else if (billingEnd.getMonth() !== selectedMonth) {
        // If calculated end date goes to previous month, use last day of current month
        billingEnd = new Date(selectedYear, selectedMonth, 0);
      }

      setFormData(prev => ({
        ...prev,
        billing_period_start: billingStart.toISOString().split('T')[0],
        billing_period_end: billingEnd.toISOString().split('T')[0],
      }));
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await customerService.getCustomers(1, 1000);
      console.log('Customers response:', response);
      
      const customerData = response.data?.customers || [];
      
      // Map customers with their packages
      const customersWithPackages = customerData.map((customer: any) => ({
        ...customer,
        package: customer.package ? {
          id: customer.package.id,
          name: customer.package.name,
          price: customer.package.price
        } : undefined
      }));
      
      setCustomers(customersWithPackages);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError('Failed to fetch customers');
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await packageService.getPackages(1, 1000);
      console.log('Packages response:', response);
      
      const packageData = response.data?.packages || [];
      setPackages(packageData);
    } catch (error) {
      console.error('Error fetching packages:', error);
      setError('Failed to fetch packages');
    }
  };

  const resetForm = () => {
    const today = new Date();

    setFormData({
      customer_id: '',
      package_id: '',
      billing_period_start: '',
      billing_period_end: '',
      amount: 0,
      tax_amount: 0,
      discount_amount: 0,
      notes: '',
    });
    setSelectedCustomer(null);
    setSelectedPackage(null);
    setSelectedMonth(today.getMonth() + 1);
    setSelectedYear(today.getFullYear());
    setError(null);
  };

  const handleCustomerChange = (event: any, newValue: CustomerWithPackage | null) => {
    setSelectedCustomer(newValue);
  };

  const handlePackageChange = (event: any, newValue: Package | null) => {
    setSelectedPackage(newValue);
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        package_id: newValue.id,
        amount: parsePackagePrice(newValue.price),
      }));
    }
  };

  const handleMonthChange = (event: any) => {
    setSelectedMonth(event.target.value);
  };

  const handleYearChange = (event: any) => {
    setSelectedYear(event.target.value);
  };

  const handleInputChange = (field: keyof CreateBillData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'amount' || field === 'tax_amount' || field === 'discount_amount' 
        ? parseFloat(value) || 0 
        : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Submitting bill with data:', formData);
      const response = await billService.createBill(formData);
      console.log('Create bill response:', response);

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error creating bill:', error);
      setError(
        error.response?.data?.message || 
        error.message || 
        'Failed to create bill'
      );
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.customer_id &&
      formData.package_id &&
      formData.billing_period_start &&
      formData.billing_period_end &&
      formData.amount > 0
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Bill</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={id}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Autocomplete
              options={customers}
              getOptionLabel={(option) => `${option.name} (${option.customer_code})`}
              value={selectedCustomer}
              onChange={handleCustomerChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer"
                  required
                  helperText={selectedCustomer?.activation_date ? 
                    `Activation Date: ${new Date(selectedCustomer.activation_date).toLocaleDateString('id-ID')} | Package: ${selectedCustomer.package?.name} - Rp ${formatPackagePrice(selectedCustomer.package?.price)}` : 
                    'Select customer to auto-calculate billing period'
                  }
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Billing Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  label="Billing Month"
                >
                  {monthOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel>Billing Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={handleYearChange}
                  label="Billing Year"
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {selectedCustomer && (
              <Alert severity="info">
                Billing period will be calculated based on customer's activation date ({new Date(selectedCustomer.activation_date || '').toLocaleDateString('id-ID')})
              </Alert>
            )}

            <Autocomplete
              options={packages}
              getOptionLabel={(option) => `${option.name} - Rp ${formatPackagePrice(option.price)}`}
              value={selectedPackage}
              onChange={handlePackageChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Package"
                  required
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Billing Period Start"
                value={formData.billing_period_start ? new Date(formData.billing_period_start).toLocaleDateString('id-ID') : ''}
                InputProps={{
                  readOnly: true,
                }}
                required
                fullWidth
                helperText="Automatically calculated based on activation date"
              />
              <TextField
                label="Billing Period End"
                value={formData.billing_period_end ? new Date(formData.billing_period_end).toLocaleDateString('id-ID') : ''}
                InputProps={{
                  readOnly: true,
                }}
                required
                fullWidth
                helperText="Automatically calculated based on activation date"
              />
            </Box>

            <Alert severity="info">
              Due date will be automatically set to the same date as billing period start
            </Alert>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange('amount')}
                required
                fullWidth
                InputProps={{
                  startAdornment: 'Rp ',
                }}
              />
              <TextField
                label="Tax Amount"
                type="number"
                value={formData.tax_amount}
                onChange={handleInputChange('tax_amount')}
                fullWidth
                InputProps={{
                  startAdornment: 'Rp ',
                }}
              />
              <TextField
                label="Discount Amount"
                type="number"
                value={formData.discount_amount}
                onChange={handleInputChange('discount_amount')}
                fullWidth
                InputProps={{
                  startAdornment: 'Rp ',
                }}
              />
            </Box>

            <TextField
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleInputChange('notes')}
              fullWidth
            />

            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1
            }}>
              <Box>
                <strong>Total Amount: Rp {(
                  (formData.amount || 0) + 
                  (formData.tax_amount || 0) - 
                  (formData.discount_amount || 0)
                ).toLocaleString('id-ID')}</strong>
              </Box>
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid()}
        >
          {loading ? 'Creating...' : 'Create Bill'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBillDialog;
